import React from 'react';
import {Col, Row, View} from '@amzn/stencil-react-components/layout';
import {Text, TextVariant} from '@amzn/stencil-react-components/text';
import {Expander, ExpanderLayout} from '@amzn/stencil-react-components/expander';
import {useTranslation} from "react-i18next";
import Footer from 'src/components/common/Footer';
import ScreenNames from 'src/constants/screen-names';
import useRecordScreenVisit from 'src/hooks/useRecordScreenVisit';
import iosDeviceIcon from 'src/images/ios_device_icon.svg';
import androidDeviceIcon from 'src/images/android_device_icon.svg';
import macDeviceIcon from 'src/images/mac_device_icon.svg';
import windowsDeviceIcon from 'src/images/windows_device_icon.svg';


/**
 * PasskeyHelp component provides information and assistance about passkeys
 */
const PasskeyHelp: React.FC = () => {
    const {t} = useTranslation();

    // Record screen visit for analytics
    useRecordScreenVisit(ScreenNames.PASSKEY_HELP);

    return (
        <>
            <Col className='centered-content' data-testid='passkey-help-content'
                 style={{zIndex: 0, maxWidth: '800px', margin: '0 auto'}}>
                <View padding={{top: 16, bottom: 20, left: 16, right: 16}} backgroundColor="white">
                    <Col gridGap="S300">
                        {/* Title */}
                        <View
                            width="600px"
                            height="72px"
                            padding="S300"
                            style={{display: 'flex', flexDirection: 'row'}}
                        >
                            <Text fontSize="T400" fontWeight="bold"
                                  color="text/primary">{t('passkey_help_title')}</Text>
                        </View>

                        {/* Section 1: What is Passkey? */}
                        <Expander
                            titleText={t('passkey_help_section_1_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Text color="neutral90">{t('passkey_help_section_1_content')}</Text>
                                <div>
                                    <Text color="neutral90">{t('passkey_help_section_1_benefits')}</Text>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text color="neutral90">{t('passkey_help_section_1_bullet_1')}</Text></li>
                                        <li><Text color="neutral90">{t('passkey_help_section_1_bullet_2')}</Text></li>
                                        <li><Text color="neutral90">{t('passkey_help_section_1_bullet_3')}</Text></li>
                                        <li><Text color="neutral90">{t('passkey_help_section_1_bullet_4')}</Text></li>
                                        <li><Text color="neutral90">{t('passkey_help_section_1_bullet_5')}</Text></li>
                                    </ul>
                                </div>
                                <Text color="neutral90">{t('passkey_help_section_1_deadline')}</Text>
                            </Col>
                        </Expander>

                        {/* Section 2: How do I set up a Passkey? */}
                        <Expander
                            titleText={t('passkey_help_section_2_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_section_2_requirements_title')}</Text>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_requirement_1')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_requirement_2')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_requirement_3')}</Text>
                                        </li>
                                    </ul>
                                </Col>

                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_section_2_setup_title')}</Text>
                                    <Text color="neutral90">{t('passkey_help_section_2_setup_intro')}</Text>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_setup_option_1')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_setup_option_2')}</Text>
                                        </li>
                                        <ul style={{listStyleType: 'circle', paddingLeft: '20px', margin: '0'}}>
                                            <li><Text
                                                color="neutral90">{t('passkey_help_section_2_setup_option_2a')}</Text>
                                            </li>
                                            <li><Text
                                                color="neutral90">{t('passkey_help_section_2_setup_option_2b')}</Text>
                                            </li>
                                        </ul>
                                    </ul>
                                </Col>

                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_section_2_process_title')}</Text>
                                    <Text color="neutral90">{t('passkey_help_section_2_process_intro')}</Text>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_process_step_1')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_process_step_2')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_process_step_3')}</Text>
                                        </li>
                                        <li><Text color="neutral90">{t('passkey_help_section_2_process_step_4')}</Text>
                                        </li>
                                    </ul>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 3: Can I log in without a Passkey? */}
                        <Expander
                            titleText={t('passkey_help_section_3_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Text color="neutral90">{t('passkey_help_section_3_content_1')}</Text>
                                <Text color="neutral90">{t('passkey_help_section_3_content_2')}</Text>
                                <Text color="neutral90">{t('passkey_help_section_3_content_3')}</Text>
                            </Col>
                        </Expander>

                        {/* Section 4: Passkeys device requirements */}
                        <Expander
                            titleText={t('passkey_help_section_4_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Col gridGap="S300">
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_section_4_mobile_title')}</Text>
                                    <Row alignItems="center" gridGap="S200">
                                        <img src={iosDeviceIcon} alt="iOS" width="24" height="24"/>
                                        <Text fontWeight="bold"
                                              color="text/primary">{t('passkey_help_section_4_ios_title')}</Text>
                                    </Row>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_ios_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_ios_requirement_2')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_ios_requirement_3')}</Text>
                                        </li>
                                    </ul>

                                    <Row alignItems="center" gridGap="S200">
                                        <img src={androidDeviceIcon} alt="Android" width="24" height="24"/>
                                        <Text fontWeight="bold"
                                              color="text/primary">{t('passkey_help_section_4_android_title')}</Text>
                                    </Row>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_android_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_android_requirement_2')}</Text>
                                        </li>
                                    </ul>
                                </Col>

                                <Col gridGap="S300">
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_section_4_desktop_title')}</Text>
                                    <Row alignItems="center" gridGap="S200">
                                        <img src={macDeviceIcon} alt="Mac" width="24" height="24"/>
                                        <Text fontWeight="bold"
                                              color="text/primary">{t('passkey_help_section_4_mac_title')}</Text>
                                    </Row>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_mac_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_mac_requirement_2')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_mac_requirement_3')}</Text>
                                        </li>
                                    </ul>

                                    <Row alignItems="center" gridGap="S200">
                                        <img src={windowsDeviceIcon} alt="Windows" width="24" height="24"/>
                                        <Text fontWeight="bold"
                                              color="text/primary">{t('passkey_help_section_4_windows_title')}</Text>
                                    </Row>
                                    <ul style={{listStyleType: 'disc', paddingLeft: '20px', margin: '0'}}>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_windows_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="neutral90">{t('passkey_help_section_4_windows_requirement_2')}</Text>
                                        </li>
                                    </ul>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 5: Updating contact information and on-site computer passwords */}
                        <Expander
                            titleText={t('passkey_help_section_5_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_5_1')}</Text>
                                    <Text color="neutral90">{t('passkey_help_faq_answer_5_1')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_5_2')}</Text>
                                    <Text color="neutral90">
                                        {t('passkey_help_faq_answer_5_2_1')}
                                        {' '}
                                        <a
                                            href={"{{PassportV1BaseUrl}}/idp/reset/resetPassword"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{color: '#0066c0', textDecoration: 'underline'}}
                                        >
                                            {t('passkey_help_faq_answer_5_2_2')}
                                        </a>
                                        {' '}
                                        {t('passkey_help_faq_answer_5_2_3')}
                                    </Text>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 6: Verifying email and phone number */}
                        <Expander
                            titleText={t('passkey_help_section_6_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({titleText, toggle}) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="text/primary"
                                    onClick={toggle}
                                    style={{cursor: 'pointer', height: '24px'}}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_6_1')}</Text>
                                    <Text color="neutral90">{t('passkey_help_faq_answer_6_1')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_6_2')}</Text>
                                    <Text color="neutral90">{t('passkey_help_faq_answer_6_2')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_6_3')}</Text>
                                    <Text color="neutral90">{t('passkey_help_faq_answer_6_3')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                          color="text/primary">{t('passkey_help_faq_question_6_4')}</Text>
                                    <Text color="neutral90">{t('passkey_help_faq_answer_6_4')}</Text>
                                </Col>
                            </Col>
                        </Expander>
                    </Col>
                </View>
                <Footer/>
            </Col>
        </>
    );
};

export default PasskeyHelp;
